'use client';

import { routing, usePathname, useRouter } from '@/i18n/routing';
import { PhoneIcon, SearchIcon } from '@/icons';
import { useHeaderStore } from '@/lib/store';
import { AuthorityInfo, Language, Menu as MenuT } from '@/lib/types';
import { createURL, NAVBAR_Z_INDEX } from '@/lib/utils';
import { removeAuthoritySegment } from '@/lib/utils/helpers';
import { CustomLink } from '@/ui';
import { Accessibility, LanguageSwitcher } from '@/widgets';
import { Box, Container, Flex, Image, Menu, Tooltip, TooltipProps, UnstyledButton } from '@mantine/core';
import { useDisclosure, useElementSize, useMediaQuery } from '@mantine/hooks';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { Suspense, useEffect } from 'react';
import classnames from './header.module.css';
import HeaderSearch from './HeaderSearch';
import Sitemap from './Sitemap';

type Props = {
  info: AuthorityInfo;
  sitemap: MenuT[];
  locale: string;
  locales: Language[];
  children: React.ReactNode;
};

type NavigationItemProps = {
  isHalfPart: boolean;
  item: MenuT;
};

const tooltipOptions = {
  zIndex: NAVBAR_Z_INDEX + 1,
  position: 'bottom',
  color: 'primary',
  withArrow: true,
  arrowSize: 5,
  maw: 220,
  multiline: true,
  className: 'text-center',
  events: { hover: true, focus: true, touch: false },
} as TooltipProps;

export default function HeaderView({ info, sitemap, locale, locales, children }: Props) {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const setHeaderHeight = useHeaderStore((store) => store.setHeight);
  const [searchOpened, { open: openSearch, close: closeSearch }] = useDisclosure(false);
  const { ref, height } = useElementSize();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isMobileOnServer = useMediaQuery('(max-width: 768px)', true);

  useEffect(() => {
    setHeaderHeight(height);
  }, [height]);

  const updatePage = (href: string) => () => {
    window.location.replace(href);
  };

  useEffect(() => {
    if (!locales.some((l: Language) => l?.code === locale)) {
      router.replace(pathname, { locale: locales?.[0]?.code || routing.defaultLocale });
    }
  }, [locale, locales]);

  const helpline = info?.helpline_value?.split(/[,;]/g)?.filter(Boolean) || [];

  const contact = () => (
    <>
      <UnstyledButton className={classnames.search} onClick={openSearch} aria-label={t('Qidiruv')}>
        <SearchIcon />
      </UnstyledButton>
      <span className={classnames.phone}>
        <PhoneIcon />
      </span>
      <div className="flex flex-col">
        {helpline?.length > 0 ? <h3 className={classnames.helpline__title}>{info?.helpline_title}</h3> : null}
        <Flex className="flex flex-wrap" columnGap="xs">
          {helpline?.map((phone) => (
            <CustomLink key={phone} href={`tel:${phone?.trim()}`} className={classnames.helpline__number}>
              {phone?.trim()};
            </CustomLink>
          ))}
        </Flex>
        <CustomLink href={`/pages/contacts`} className={classnames.helpline__contact}>
          {info?.contact_title}
        </CustomLink>
      </div>
      <Tooltip {...tooltipOptions} label={t('Tilni tanlang')}>
        <div>
          <LanguageSwitcher locale={locale} locales={locales} />
        </div>
      </Tooltip>
    </>
  );

  return (
    <>
      <header className={classnames.header} ref={ref}>
        <Container size="100rem" className="relative flex flex-col">
          <div className="flex justify-between items-center gap-[0.625rem] py-6 pr-0 3xl:py-3 lg:pr-[34px]">
            <Box className={classnames.home_link} component={CustomLink} onClick={updatePage('/')} href="/">
              <Image src={info?.logo} alt="Logo" className={clsx(classnames.header_logo, 'object-contain')} />
              <h2 className={classnames.title}>{info?.title}</h2>
            </Box>
            <Box>
              {isMobile ? null : (
                <div className=" flex items-center gap-[1.25rem] lg:gap-[0.75rem] 2xl:gap-[1.5rem] shrink-0 lg:hidden">
                  {contact()}
                  <Accessibility />
                </div>
              )}
              <Sitemap data={sitemap} isMobile />
            </Box>
          </div>
          {isMobileOnServer ? (
            <div className="flex justify-center items-center gap-[1.25rem] hidden shrink-0 pb-3 lg:flex">
              {contact()}
              <Accessibility />
            </div>
          ) : null}
          {isMobile ? null : (
            <Box className={classnames.header__navigation}>
              <Flex
                justify="center"
                wrap="wrap"
                px="md"
                className={clsx(classnames.header__menu, 'flex justify-center flex-wrap')}
              >
                {sitemap?.length > 0
                  ? sitemap?.map((item, i) => (
                      <NavigationItem key={item?.id} isHalfPart={i > sitemap.length / 2} item={item} />
                    ))
                  : null}
              </Flex>
              <Sitemap data={sitemap} />
            </Box>
          )}
        </Container>
      </header>
      <Suspense>
        <HeaderSearch opened={searchOpened} close={closeSearch}>
          {children}
        </HeaderSearch>
      </Suspense>
    </>
  );
}

function NavigationItem({ isHalfPart, item }: NavigationItemProps) {
  const pathname = usePathname();
  const isMenuActive = item?.children?.some((child) => removeAuthoritySegment(`/${child?.url}`) === pathname);

  return (
    <Menu
      keepMounted
      key={item?.id}
      position={isHalfPart ? 'bottom-end' : 'bottom-start'}
      trigger="click-hover"
      loop={false}
      withinPortal={false}
      menuItemTabIndex={0}
      offset={0}
      closeDelay={0}
      classNames={classnames}
    >
      <Menu.Target>
        <UnstyledButton className={classnames.menu__item} data-active={isMenuActive}>
          {item?.title}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown bg="primary">
        {item?.children?.map((child) => (
          <Menu.Item
            key={child?.id}
            component={CustomLink}
            target={child?.url?.includes('http') ? '_blank' : '_self'}
            href={createURL(child?.url)}
            data-active={removeAuthoritySegment(`/${child?.url}`) === pathname}
          >
            {child?.title}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
